import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  FileText, Search, Filter, Trash2, Folder, FolderPlus, FolderOpen,
  Users, X, Plus, ChevronRight, Calendar, ArrowLeft, Building2,
  User as UserIcon, Edit2, Link2, Loader2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { HUB_DEFINITIONS } from '../lib/hubPrompts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FOLDER_COLORS = [
  { key: 'blue', class: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-950/30' },
  { key: 'emerald', class: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-950/30' },
  { key: 'amber', class: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-950/30' },
  { key: 'rose', class: 'text-rose-500', bg: 'bg-rose-100 dark:bg-rose-950/30' },
  { key: 'violet', class: 'text-violet-500', bg: 'bg-violet-100 dark:bg-violet-950/30' },
  { key: 'sky', class: 'text-sky-500', bg: 'bg-sky-100 dark:bg-sky-950/30' },
];

export default function AIDocuments() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [tab, setTab] = useState('all');
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [clientGroups, setClientGroups] = useState([]);
  const [openFolderId, setOpenFolderId] = useState(null);
  const [openFolderDocs, setOpenFolderDocs] = useState([]);
  const [openClientId, setOpenClientId] = useState(null);
  const [openClientDocs, setOpenClientDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ hub: 'all', tag: 'all', search: '', doc_type: 'all' });
  const [viewDoc, setViewDoc] = useState(null);
  const [folderDialog, setFolderDialog] = useState(null); // null|{} for create|{folder} for edit

  const fetchAll = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.hub !== 'all') params.set('hub', filters.hub);
    if (filters.tag !== 'all') params.set('tag', filters.tag);
    if (filters.doc_type !== 'all') params.set('doc_type', filters.doc_type);
    if (filters.search) params.set('search', filters.search);
    try {
      const [dRes, fRes, cRes] = await Promise.all([
        apiFetch(`${API_URL}/api/workspaces/${wsId}/ai-documents?${params}`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/folders`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/contacts-with-documents`),
      ]);
      if (dRes.ok) setDocs(await dRes.json());
      if (fRes.ok) setFolders(await fRes.json());
      if (cRes.ok) setClientGroups(await cRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId, filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Load folder docs when folder opened
  useEffect(() => {
    if (openFolderId) {
      apiFetch(`${API_URL}/api/folders/${openFolderId}/documents`)
        .then(r => r.ok ? r.json() : [])
        .then(setOpenFolderDocs)
        .catch(() => {});
    }
  }, [openFolderId]);

  // Load contact docs when contact opened
  useEffect(() => {
    if (openClientId) {
      apiFetch(`${API_URL}/api/contacts/${openClientId}/ai-documents`)
        .then(r => r.ok ? r.json() : [])
        .then(setOpenClientDocs)
        .catch(() => {});
    }
  }, [openClientId]);

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    await apiFetch(`${API_URL}/api/ai-documents/${docId}`, { method: 'DELETE' });
    toast.success('Document deleted');
    fetchAll();
    if (openFolderId) setOpenFolderDocs(prev => prev.filter(d => d.document_id !== docId));
    if (openClientId) setOpenClientDocs(prev => prev.filter(d => d.document_id !== docId));
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Documents will be moved to "All".')) return;
    await apiFetch(`${API_URL}/api/folders/${folderId}`, { method: 'DELETE' });
    toast.success('Folder deleted');
    if (openFolderId === folderId) setOpenFolderId(null);
    fetchAll();
  };

  const allTags = [...new Set(docs.flatMap(d => d.tags || []))];
  const allTypes = [...new Set(docs.map(d => d.doc_type).filter(Boolean))];
  const hasFilters = filters.hub !== 'all' || filters.tag !== 'all' || filters.doc_type !== 'all' || filters.search;

  // Folder being viewed
  const currentFolder = openFolderId ? folders.find(f => f.folder_id === openFolderId) : null;
  const currentClient = openClientId ? clientGroups.find(c => c.contact_id === openClientId) : null;

  return (
    <div className="space-y-6" data-testid="ai-documents-page">
      <div className="rounded-2xl border bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-emerald-500/10 p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white shadow-lg">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Documents Library</h1>
              <p className="text-sm text-muted-foreground">All your AI-generated documents — organize with folders, browse by client, or search across everything.</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setOpenFolderId(null); setOpenClientId(null); }}>
        <TabsList>
          <TabsTrigger value="all" data-testid="docs-tab-all">
            <FileText className="w-3.5 h-3.5 mr-1.5" /> All Documents
            <Badge variant="secondary" className="ml-2 text-[10px]">{docs.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="folders" data-testid="docs-tab-folders">
            <Folder className="w-3.5 h-3.5 mr-1.5" /> Folders
            <Badge variant="secondary" className="ml-2 text-[10px]">{folders.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="by-client" data-testid="docs-tab-by-client">
            <Users className="w-3.5 h-3.5 mr-1.5" /> By Client / Vendor
            <Badge variant="secondary" className="ml-2 text-[10px]">{clientGroups.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* ALL DOCUMENTS TAB */}
        <TabsContent value="all" className="space-y-4 mt-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-3">
              <div className="flex flex-wrap gap-2 items-center">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input placeholder="Search documents..." value={filters.search}
                    onChange={e => setFilters({ ...filters, search: e.target.value })}
                    className="pl-9 h-8 text-sm" data-testid="doc-search" />
                </div>
                <Select value={filters.hub} onValueChange={v => setFilters({ ...filters, hub: v })}>
                  <SelectTrigger className="w-36 h-8 text-xs" data-testid="doc-hub-filter">
                    <SelectValue placeholder="All Hubs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Hubs</SelectItem>
                    {Object.values(HUB_DEFINITIONS).map(h => (
                      <SelectItem key={h.key} value={h.key}>{h.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {allTypes.length > 0 && (
                  <Select value={filters.doc_type} onValueChange={v => setFilters({ ...filters, doc_type: v })}>
                    <SelectTrigger className="w-32 h-8 text-xs" data-testid="doc-type-filter">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {allTypes.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {allTags.length > 0 && (
                  <Select value={filters.tag} onValueChange={v => setFilters({ ...filters, tag: v })}>
                    <SelectTrigger className="w-32 h-8 text-xs" data-testid="doc-tag-filter">
                      <SelectValue placeholder="All Tags" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tags</SelectItem>
                      {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {hasFilters && (
                  <Button variant="ghost" size="sm" onClick={() => setFilters({ hub: 'all', tag: 'all', search: '', doc_type: 'all' })}>
                    <X className="w-3 h-3 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <DocsGrid docs={docs} loading={loading} onOpen={setViewDoc} onDelete={handleDelete} />
        </TabsContent>

        {/* FOLDERS TAB */}
        <TabsContent value="folders" className="space-y-4 mt-4">
          {openFolderId && currentFolder ? (
            <FolderView
              folder={currentFolder}
              docs={openFolderDocs}
              onBack={() => setOpenFolderId(null)}
              onOpenDoc={setViewDoc}
              onDelete={handleDelete}
              onEdit={() => setFolderDialog(currentFolder)}
            />
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Organize documents into folders. Link folders to clients/vendors to auto-include their documents.</p>
                <Button size="sm" onClick={() => setFolderDialog({})} data-testid="new-folder-btn">
                  <FolderPlus className="w-4 h-4 mr-1.5" /> New Folder
                </Button>
              </div>
              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
                </div>
              ) : folders.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-16 text-center">
                    <Folder className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm text-muted-foreground">No folders yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Create your first folder to organize documents</p>
                    <Button size="sm" className="mt-3" onClick={() => setFolderDialog({})}>
                      <FolderPlus className="w-4 h-4 mr-1.5" /> Create Folder
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {folders.map(f => {
                    const colorCfg = FOLDER_COLORS.find(c => c.key === f.color) || FOLDER_COLORS[0];
                    return (
                      <button
                        key={f.folder_id}
                        onClick={() => setOpenFolderId(f.folder_id)}
                        className="text-left rounded-xl border bg-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all group"
                        data-testid={`folder-${f.folder_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colorCfg.bg)}>
                            <Folder className={cn("w-5 h-5", colorCfg.class)} />
                          </div>
                          <span className="text-[10px] text-muted-foreground">{f.document_count} doc{f.document_count === 1 ? '' : 's'}</span>
                        </div>
                        <h3 className="text-sm font-semibold mt-2.5 line-clamp-1">{f.name}</h3>
                        {f.description && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{f.description}</p>}
                        {(f.linked_contacts || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            <Link2 className="w-3 h-3 text-muted-foreground" />
                            {f.linked_contacts.slice(0, 2).map(c => (
                              <Badge key={c.contact_id} variant="outline" className="text-[9px]">{c.name}</Badge>
                            ))}
                            {f.linked_contacts.length > 2 && <Badge variant="outline" className="text-[9px]">+{f.linked_contacts.length - 2}</Badge>}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* BY CLIENT TAB */}
        <TabsContent value="by-client" className="space-y-4 mt-4">
          {openClientId && currentClient ? (
            <ClientDocsView
              client={currentClient}
              docs={openClientDocs}
              onBack={() => setOpenClientId(null)}
              onOpenDoc={setViewDoc}
              onDelete={handleDelete}
            />
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Browse documents grouped by the client or vendor they're attached to.</p>
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
              ) : clientGroups.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-16 text-center">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm text-muted-foreground">No client-attached documents yet</p>
                    <p className="text-xs text-muted-foreground mt-1">When saving an AI doc, attach it to a client/vendor — they'll appear here.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {clientGroups.map(c => (
                    <button
                      key={c.contact_id}
                      onClick={() => setOpenClientId(c.contact_id)}
                      className="text-left rounded-xl border bg-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center gap-3"
                      data-testid={`client-group-${c.contact_id}`}
                    >
                      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                        c.contact_type === 'customer' ? "bg-emerald-100 dark:bg-emerald-950/30" : "bg-blue-100 dark:bg-blue-950/30"
                      )}>
                        {c.contact_type === 'customer'
                          ? <UserIcon className={cn("w-5 h-5", "text-emerald-600 dark:text-emerald-400")} />
                          : <Building2 className={cn("w-5 h-5", "text-blue-600 dark:text-blue-400")} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold truncate">{c.name}</h3>
                        <p className="text-xs text-muted-foreground capitalize">{c.contact_type} · {c.doc_count} document{c.doc_count === 1 ? '' : 's'}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Doc viewer */}
      <Dialog open={!!viewDoc} onOpenChange={(v) => !v && setViewDoc(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> {viewDoc?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-1 -mt-1">
            {viewDoc?.hub && <Badge variant="secondary" className="text-[10px] capitalize">{viewDoc.hub}</Badge>}
            {(viewDoc?.tags || []).map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
            {(viewDoc?.attached_contacts || []).map(c => (
              <Badge key={c.contact_id} className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30">
                <Link to={`/contacts/${c.contact_id}`} className="hover:underline">{c.name}</Link>
              </Badge>
            ))}
          </div>
          <div className="overflow-y-auto flex-1 mt-3 pr-2">
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{viewDoc?.content}</pre>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(viewDoc?.content || ''); toast.success('Copied'); }}>Copy</Button>
            <Button onClick={() => setViewDoc(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder create/edit dialog */}
      <FolderDialog
        open={!!folderDialog}
        folder={folderDialog?.folder_id ? folderDialog : null}
        wsId={wsId}
        onOpenChange={(v) => !v && setFolderDialog(null)}
        onSaved={() => { setFolderDialog(null); fetchAll(); }}
        onDelete={folderDialog?.folder_id ? () => { deleteFolder(folderDialog.folder_id); setFolderDialog(null); } : null}
      />
    </div>
  );
}

function DocsGrid({ docs, loading, onOpen, onDelete }) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }
  if (docs.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-16 text-center">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm text-muted-foreground">No documents</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {docs.map(d => {
        const hub = HUB_DEFINITIONS[d.hub];
        return (
          <Card key={d.document_id} className="hover:shadow-md transition-shadow cursor-pointer group" onClick={() => onOpen(d)} data-testid={`doc-card-${d.document_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <FileText className="w-5 h-5 text-muted-foreground" />
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); onDelete(d.document_id); }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <h3 className="text-sm font-semibold line-clamp-2">{d.title}</h3>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1.5 leading-relaxed">{(d.content || '').slice(0, 120)}</p>
              <div className="flex flex-wrap gap-1 mt-2.5">
                {hub && <Badge variant="secondary" className="text-[9px] capitalize">{hub.name.replace(' Hub', '')}</Badge>}
                {d.doc_type && d.doc_type !== 'general' && <Badge variant="outline" className="text-[9px] capitalize">{d.doc_type}</Badge>}
                {(d.tags || []).slice(0, 2).map(t => <Badge key={t} variant="outline" className="text-[9px]">{t}</Badge>)}
              </div>
              {(d.attached_contacts || []).length > 0 && (
                <div className="text-[10px] text-muted-foreground mt-1.5 flex items-center gap-1">
                  <UserIcon className="w-3 h-3" />
                  {d.attached_contacts.map(c => c.name).join(', ')}
                </div>
              )}
              <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" />{new Date(d.updated_at).toLocaleDateString()}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function FolderView({ folder, docs, onBack, onOpenDoc, onDelete, onEdit }) {
  const colorCfg = FOLDER_COLORS.find(c => c.key === folder.color) || FOLDER_COLORS[0];
  return (
    <div className="space-y-4" data-testid="folder-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="folder-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0", colorCfg.bg)}>
            <FolderOpen className={cn("w-5 h-5", colorCfg.class)} />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold truncate">{folder.name}</h2>
            <p className="text-xs text-muted-foreground">
              {docs.length} document{docs.length === 1 ? '' : 's'}
              {(folder.linked_contacts || []).length > 0 && ` · linked to ${folder.linked_contacts.length} contact${folder.linked_contacts.length === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onEdit} data-testid="edit-folder-btn">
          <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Edit Folder
        </Button>
      </div>

      {(folder.linked_contacts || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <Link2 className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Linked:</span>
          {folder.linked_contacts.map(c => (
            <Badge key={c.contact_id} variant="secondary" className="text-[10px]">
              <Link to={`/contacts/${c.contact_id}`} className="hover:underline">{c.name}</Link>
            </Badge>
          ))}
        </div>
      )}

      <DocsGrid docs={docs} loading={false} onOpen={onOpenDoc} onDelete={onDelete} />
    </div>
  );
}

function ClientDocsView({ client, docs, onBack, onOpenDoc, onDelete }) {
  return (
    <div className="space-y-4" data-testid="client-docs-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="client-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center",
          client.contact_type === 'customer' ? "bg-emerald-100 dark:bg-emerald-950/30" : "bg-blue-100 dark:bg-blue-950/30"
        )}>
          {client.contact_type === 'customer' ? <UserIcon className="w-5 h-5 text-emerald-600" /> : <Building2 className="w-5 h-5 text-blue-600" />}
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold">{client.name}</h2>
          <p className="text-xs text-muted-foreground capitalize">{client.contact_type} · {docs.length} document{docs.length === 1 ? '' : 's'}</p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to={`/contacts/${client.contact_id}`}>Open Contact</Link>
        </Button>
      </div>
      <DocsGrid docs={docs} loading={false} onOpen={onOpenDoc} onDelete={onDelete} />
    </div>
  );
}

function FolderDialog({ open, folder, wsId, onOpenChange, onSaved, onDelete }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('blue');
  const [contacts, setContacts] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(folder?.name || '');
      setDescription(folder?.description || '');
      setColor(folder?.color || 'blue');
      setSelectedContactIds(folder?.linked_contact_ids || []);
      setContactSearch('');
      if (wsId) {
        apiFetch(`${API_URL}/api/contacts?workspace_id=${wsId}`)
          .then(r => r.ok ? r.json() : [])
          .then(setContacts)
          .catch(() => {});
      }
    }
  }, [open, folder, wsId]);

  const filteredContacts = contacts.filter(c =>
    !contactSearch || c.name?.toLowerCase().includes(contactSearch.toLowerCase())
  );

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: name.trim(), description, color, linked_contact_ids: selectedContactIds };
      const url = folder?.folder_id
        ? `${API_URL}/api/folders/${folder.folder_id}`
        : `${API_URL}/api/workspaces/${wsId}/folders`;
      const method = folder?.folder_id ? 'PATCH' : 'POST';
      const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) {
        toast.success(folder?.folder_id ? 'Folder updated' : 'Folder created');
        onSaved();
      } else {
        toast.error('Failed to save folder');
      }
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="folder-dialog">
        <DialogHeader>
          <DialogTitle>{folder?.folder_id ? 'Edit Folder' : 'New Folder'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Name *</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Acme Contracts" data-testid="folder-name-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Color</Label>
            <div className="flex gap-2">
              {FOLDER_COLORS.map(c => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setColor(c.key)}
                  className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center border-2 transition-all",
                    c.bg,
                    color === c.key ? "border-foreground scale-110" : "border-transparent"
                  )}
                  data-testid={`color-${c.key}`}
                >
                  <Folder className={cn("w-4 h-4", c.class)} />
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Link to Clients / Vendors (optional)</Label>
            <p className="text-[10px] text-muted-foreground">Documents attached to these contacts will automatically appear in this folder.</p>
            {selectedContactIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1.5">
                {selectedContactIds.map(cid => {
                  const c = contacts.find(x => x.contact_id === cid);
                  return (
                    <Badge key={cid} className="text-[10px] bg-emerald-100 text-emerald-700 pr-0.5">
                      {c?.name || cid}
                      <button onClick={() => setSelectedContactIds(prev => prev.filter(x => x !== cid))} className="ml-1 hover:bg-black/10 rounded-full p-0.5">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input value={contactSearch} onChange={e => setContactSearch(e.target.value)} placeholder="Search clients/vendors..." className="pl-9 h-8" />
            </div>
            {contactSearch && (
              <div className="max-h-32 overflow-y-auto rounded-md border bg-card">
                {filteredContacts.slice(0, 8).map(c => (
                  <button
                    key={c.contact_id}
                    type="button"
                    onClick={() => {
                      if (!selectedContactIds.includes(c.contact_id)) {
                        setSelectedContactIds(prev => [...prev, c.contact_id]);
                      }
                      setContactSearch('');
                    }}
                    className="w-full text-left px-3 py-1.5 hover:bg-muted/50 text-sm flex items-center justify-between"
                  >
                    <span>{c.name}</span>
                    <Badge variant="secondary" className="text-[9px] capitalize">{c.contact_type}</Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          {onDelete && (
            <Button variant="destructive" onClick={onDelete} className="mr-auto" data-testid="delete-folder-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!name.trim() || saving} data-testid="save-folder-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            {folder?.folder_id ? 'Save Changes' : 'Create Folder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
